<script setup lang="ts">
  import type { IMenuItem, TCountryRestrictedLink } from '@/components/sidebar-menu/types'
  import { useLocaleStore } from '@/stores/Locale.store'
  import { getCompactLinkData } from '@/utils/storyblok/getCompactLinkData'
  import { getSidebarMenuVariant, SIDEBAR_MENU_VARIANTS } from '@/utils/constants/sidebarMenu'
  import type { SidebarLinksStoryblok } from '@/storyblok/component-types-sb'
  import type { ISbLink } from '@/storyblok/types'

  const localeStore = useLocaleStore()

  const { locale } = useI18n()
  const { currentRoute } = useRouter()

  const props = withDefaults(
    defineProps<{
      blok: SidebarLinksStoryblok
      showMobile?: boolean
      showDesktop?: boolean
    }>(),
    {
      showMobile: false,
      showDesktop: false
    }
  )

  const desktopVariant = computed(() => getSidebarMenuVariant(props.blok.variant))

  const translatedRoute = (url?: string) => {
    if (!url) return ''

    const normalizedUrl = url.replace(/^\/+/, '')

    if (normalizedUrl === locale.value || normalizedUrl.startsWith(`${locale.value}/`)) {
      return `/${normalizedUrl}`
    }

    return `/${locale.value}/${normalizedUrl}`
  }

  const isLinkAllowedForCountry = (link: TCountryRestrictedLink) => {
    if (!link || typeof link === 'string') return true

    const linkContent = 'content' in link ? link.content : link
    const whitelistCountries = linkContent?.whitelistCountries

    return !whitelistCountries?.length || whitelistCountries.includes(localeStore.selectedCountry)
  }

  const getLocalizedCompactLinkData = (link: SidebarLinksStoryblok['logoLink']) => {
    const compactLink = getCompactLinkData(link)

    if (!link || typeof link === 'string') return compactLink

    const href = 'content' in link ? link.content?.href : link.href

    if (!href?.url && href?.cached_url) {
      return {
        ...compactLink,
        href: translatedRoute(href.cached_url)
      }
    }

    return compactLink
  }

  const isActiveLink = (href: string) => {
    return currentRoute.value.path === href
  }

  const menuItems = computed<IMenuItem[]>(() => {
    return props.blok.items.reduce((menuItem: IMenuItem[], item: any) => {
      if (typeof item === 'string') return menuItem

      if (!isLinkAllowedForCountry(item)) {
        return menuItem
      }

      const url = item.content?.href?.url ?? ''
      const cachedUrl = item.content?.href?.cached_url ?? ''
      const href = url || translatedRoute(cachedUrl)

      if (!href) return menuItem

      const isActive = isActiveLink(url) || isActiveLink(translatedRoute(cachedUrl))

      menuItem.push({
        name: item.content.text,
        href,
        active: isActive,
        target: item.content?.href?.target === '_blank' ? '_blank' : '_self'
      })

      return menuItem
    }, [])
  })

  const logoLink = computed<ISbLink | undefined>(() => {
    if (!isLinkAllowedForCountry(props.blok.logoLink)) return undefined

    const compactLink = getLocalizedCompactLinkData(props.blok.logoLink)

    if (!compactLink.href) return undefined

    return compactLink
  })

  const ctaLink = computed<ISbLink | undefined>(() => {
    if (!isLinkAllowedForCountry(props.blok.ctaLink)) return undefined

    const compactLink = getLocalizedCompactLinkData(props.blok.ctaLink)

    if (!compactLink.href) return undefined

    return {
      ...compactLink,
      text: props.blok.ctaLinkTextOverride || compactLink.text
    }
  })

  const logoMenuItem = computed<IMenuItem | undefined>(() => {
    if (
      desktopVariant.value !== SIDEBAR_MENU_VARIANTS.horizontal ||
      !logoLink.value?.href ||
      !props.blok.logoLinkMobileText
    ) {
      return undefined
    }

    return {
      name: props.blok.logoLinkMobileText,
      href: logoLink.value.href,
      active: isActiveLink(logoLink.value.href),
      highlighted: !menuItems.value.some((item) => item.active),
      target: logoLink.value.target
    }
  })
</script>

<template>
  <div v-editable="blok">
    <SidebarMenuMobile
      v-if="showMobile"
      :menu-items="logoMenuItem ? [logoMenuItem, ...menuItems] : menuItems"
    />
    <SidebarMenuDesktop
      v-if="showDesktop && desktopVariant === SIDEBAR_MENU_VARIANTS.vertical"
      :menu-items="menuItems"
    />
    <SidebarMenuDesktopHorizontal
      v-if="showDesktop && desktopVariant === SIDEBAR_MENU_VARIANTS.horizontal"
      :menu-items="menuItems"
      :logo-image="blok.logoImage"
      :logo-link="logoLink"
      :cta-link="ctaLink"
    />
  </div>
</template>
